import { useMemo } from 'react';
import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import type { Donem } from './donem';
import type { SatisKaynaklari } from './ekranKaynaklari';
import { parametreBildiriyor, useSaSorgu } from './useSatisVerisi';
import { KapsamNotu } from './KapsamNotu';
import { BlokIskeleti } from './BlokIskeleti';
import { KOMISYON_NOTU, komisyonNotuGerekirMi } from './kanal';
import { sayi, type Sayisal } from './ozetHesaplari';
import type { ZararSatiri } from './uyarilar';
import { oranMetni } from './bicim';

interface UrunKar {
  ciro: Sayisal;
  maliyet: Sayisal;
  kar: Sayisal;
}

export interface Kopru {
  ciro: number;
  maliyet: number;
  kar: number;
  marj: number | null;
}

/**
 * Kopru RAPORUN `kar` kolonundan turer, `ciro - maliyet`ten DEGIL: ciro KDV DAHIL (`dahil_tutar`), kar
 * KDV HARIC (`tutar`) uzerinden; fark KDV kadar sisik bir "kar" uretirdi.
 */
export function kopruHesapla(satirlar: UrunKar[]): Kopru {
  let ciro = 0;
  let maliyet = 0;
  let kar = 0;
  for (const s of satirlar) {
    ciro += sayi(s.ciro);
    maliyet += sayi(s.maliyet);
    kar += sayi(s.kar);
  }
  return { ciro, maliyet, kar, marj: ciro > 0 ? (kar / ciro) * 100 : null };
}

interface Props {
  kaynaklar: Pick<SatisKaynaklari, 'urunKarliligi' | 'zararinaSatis'>;
  donem: Donem;
  sube?: string | null;
  kanal?: string | null;
}

/**
 * Tikla-ac — kar koprusu. G2: 990056 (990016 ifadeleri + kanal) ve 990054 (990033 tanimi, kart maliyeti).
 * 990054 anahtari Uyarilar blogu ile paylasilir. Zarar kopruden CIKARILMAZ (990056 zararli satirlari zaten
 * kapsar; cift sayardi).
 */
export function KarKoprusuBlok({ kaynaklar, donem, sube = null, kanal = null }: Props) {
  const f = { donem, sube, kanal };
  const karQ = useSaSorgu<UrunKar>(kaynaklar.urunKarliligi, f);
  const zararQ = useSaSorgu<ZararSatiri>(kaynaklar.zararinaSatis, f);

  const kopru = useMemo(() => kopruHesapla(karQ.data?.data ?? []), [karQ.data]);
  const zarar = useMemo(
    () => (zararQ.data?.data ?? []).reduce((t, r) => t + Math.abs(sayi(r.zarar)), 0),
    [zararQ.data],
  );

  if (!kaynaklar.urunKarliligi) return null;
  if (karQ.isLoading)
    return <BlokIskeleti testid="blok-kopru-iskelet" altBaslik="Ciro → maliyet → brüt kâr" yukseklik={220} satir={4} />;
  if (kopru.ciro === 0 && kopru.kar === 0) return null;

  const taban = Math.max(kopru.ciro, kopru.maliyet, Math.abs(kopru.kar), 1);
  const pay = (v: number) => Math.max(0, Math.min(100, (Math.abs(v) / taban) * 100));
  const cubuklar = [
    { etiket: 'Ciro', deger: kopru.ciro, renk: 'var(--color-primary)', testid: 'kopru-ciro' },
    { etiket: 'Maliyet', deger: kopru.maliyet, renk: 'var(--color-warning)', testid: 'kopru-maliyet' },
    {
      etiket: 'Brüt kâr',
      deger: kopru.kar,
      renk: kopru.kar >= 0 ? 'var(--color-positive)' : 'var(--color-negative)',
      testid: 'kopru-kar',
    },
  ];

  return (
    <div data-testid="blok-kar-koprusu">
      <SectionCard
        // Baslik BASILMAZ: TiklaAc kabugu ayni metni bir kez zaten gosteriyor (bkz. TiklaAc.tsx).
        subtitle={
          <>
            Ciro → maliyet → brüt kâr
            <KapsamNotu sube={sube} destekli={parametreBildiriyor(kaynaklar.urunKarliligi, '@LOKASYON')} />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {cubuklar.map((c) => (
            <div key={c.etiket} data-testid={c.testid} className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[12.5px] text-ink-600">{c.etiket}</span>
                <span className="text-[13px] tnum font-medium text-ink-900">{fmtTRY(c.deger)}</span>
              </div>
              <div className="h-2 rounded-full bg-border-light overflow-hidden">
                <div data-testid={`${c.testid}-fill`} style={{ width: `${pay(c.deger)}%`, background: c.renk, height: '100%' }} />
              </div>
            </div>
          ))}

          <div className="flex items-baseline justify-between gap-3 pt-1 border-t border-border">
            <span className="text-[12.5px] text-ink-600">Brüt kâr marjı</span>
            <span className="text-[13px] tnum font-medium text-ink-900" data-testid="kopru-marj">
              {kopru.marj === null ? '—' : oranMetni(kopru.marj, 1)}
            </span>
          </div>

          {kaynaklar.zararinaSatis && zarar > 0 && (
            <div className="flex items-baseline justify-between gap-3" data-testid="kopru-zarar">
              <span className="text-[12px] text-ink-500">
                Bunun içinde zararına satış
                <span className="text-[11px] text-ink-400"> · kart maliyeti</span>
              </span>
              <span className="text-[12.5px] tnum text-neg-700">−{fmtTRY(zarar)}</span>
            </div>
          )}

          {komisyonNotuGerekirMi(kanal) && (
            <span data-testid="komisyon-notu" className="text-[12px] text-warn-700">{KOMISYON_NOTU}</span>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
